import { api } from "@/app/services/api.service";
import { extractErrorMessage } from "./mobileApiUtils";

export { extractErrorMessage };

export const ANNOUNCEMENTS_PATH = "configuration/announcements";

export type Announcement = {
  id: string;
  title: string;
  description: string;
  coverImagePath?: string | null;
  coverImage?: string | null;
  status?: string | null;
  active?: boolean;
};

export function getAnnouncementCoverUrl(imagePath: string | null | undefined) {
  if (!imagePath) return "";

  const trimmed = imagePath.trim();
  if (!trimmed) return "";

  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    return trimmed;
  }

  const base = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");
  const normalized = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;

  if (normalized.startsWith("/configuration/")) {
    return `${base}${normalized}`;
  }

  if (normalized.startsWith("/files")) {
    return `${base}/configuration${normalized}`;
  }

  return `${base}/configuration/files?attachment=${encodeURIComponent(trimmed)}`;
}

function resolveCoverPath(item: Record<string, unknown>) {
  const path = item.coverImagePath ?? item.coverImage ?? item.imagePath ?? item.image;
  return path != null ? String(path) : null;
}

export function normalizeAnnouncement(item: unknown): Announcement | null {
  if (item == null || typeof item !== "object") return null;
  const record = item as Record<string, unknown>;
  const id = record.id != null ? String(record.id) : "";
  const title = record.title != null ? String(record.title) : "";
  const description = record.description != null ? String(record.description) : "";
  if (!id && !title) return null;
  const status = record.status != null ? String(record.status) : null;
  return {
    id,
    title,
    description,
    coverImagePath: resolveCoverPath(record),
    status,
    active: record.active != null ? Boolean(record.active) : status === "ACTIVE",
  };
}

export function extractAnnouncementList(body: unknown): Announcement[] {
  if (body == null) return [];
  if (Array.isArray(body)) {
    return body
      .map((item) => normalizeAnnouncement(item))
      .filter((item): item is Announcement => item != null);
  }
  const obj = body as Record<string, unknown>;
  const candidates = [obj.data, obj.content, obj.items, obj.records, obj.announcements];
  for (const candidate of candidates) {
    const parsed = extractAnnouncementList(candidate);
    if (parsed.length > 0) return parsed;
  }
  return [];
}

export async function fetchAnnouncements(): Promise<Announcement[]> {
  const res = await api().get(ANNOUNCEMENTS_PATH);
  const root = res.data;
  if (Array.isArray(root)) return extractAnnouncementList(root);
  if (root != null && typeof root === "object") {
    const rootObj = root as Record<string, unknown>;
    if (Array.isArray(rootObj.data)) return extractAnnouncementList(rootObj.data);
    return extractAnnouncementList(root);
  }
  return [];
}

export async function fetchAnnouncement(id: string): Promise<Announcement | null> {
  const res = await api().get(`${ANNOUNCEMENTS_PATH}/${id}`);
  const root = res.data;
  if (root != null && typeof root === "object") {
    const rootObj = root as Record<string, unknown>;
    if (rootObj.data != null) return normalizeAnnouncement(rootObj.data);
    return normalizeAnnouncement(root);
  }
  return null;
}

export async function createAnnouncement(formData: FormData) {
  await api().post(ANNOUNCEMENTS_PATH, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

export async function updateAnnouncement(id: string, formData: FormData) {
  await api().post(`${ANNOUNCEMENTS_PATH}/${id}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

export async function deleteAnnouncement(id: string) {
  await api().delete(`${ANNOUNCEMENTS_PATH}/${id}`);
}

export function buildAnnouncementFormData(
  payload: { title: string; description: string; coverImage?: File | null },
) {
  const formData = new FormData();
  formData.append("title", payload.title);
  formData.append("description", payload.description);
  if (payload.coverImage) formData.append("coverImage", payload.coverImage);
  return formData;
}
